import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import UserLanguageSwitcher from "../components/common/UserLanguageSwitcher";
import { INITIAL_MARKETS, CRYPTO_ICONS } from "../constants/markets";
import "../components/dashboard/Dashboard.css";
import "./MarketPage.css";

const MarketPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // FIX: Load from Cache immediately so user NEVER sees "dummy" 92201 prices
  const [markets, setMarkets] = useState(() => {
    const saved = localStorage.getItem("last_market_prices");
    return saved ? JSON.parse(saved) : INITIAL_MARKETS;
  });

  const wsRef = useRef(null);
  const eurRateRef = useRef(0.92);
  const lastUpdateRef = useRef(Date.now());

  useEffect(() => {
    let isMounted = true;
    let watchdogInterval = null;

    const connect = () => {
      if (!isMounted) return;

      // Kill any "zombie" connections
      if (wsRef.current) {
        wsRef.current.onclose = null;
        wsRef.current.onerror = null;
        wsRef.current.close();
      }

      const cryptoStreams = INITIAL_MARKETS.filter(
        (m) => !m.symbol.includes("XAU"),
      ).map((m) => `${m.pair.replace("/", "").toLowerCase()}@miniTicker`);
      const streams = [...cryptoStreams, "paxgusdt@miniTicker"];
      const wsUrl = `wss://stream.binance.com:9443/stream?streams=${streams.join("/")}`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        if (!isMounted) return;
        lastUpdateRef.current = Date.now(); // Feed the watchdog

        try {
          const message = JSON.parse(event.data);
          const ticker = message.data || message;
          const symbol = ticker.s;
          if (!symbol) return;

          const closePrice = parseFloat(ticker.c);
          const openPrice = parseFloat(ticker.o);

          if (symbol === "EURUSDT") {
            eurRateRef.current = 1 / closePrice;
            return;
          }

          setMarkets((prev) => {
            const newState = prev.map((m) => {
              const mSymbol = m.pair.replace("/", "").toUpperCase();
              const priceChangePercent =
                openPrice > 0
                  ? ((closePrice - openPrice) / openPrice) * 100
                  : 0;
              const isUp = priceChangePercent >= 0;
              const changeText = `${isUp ? "+" : ""}${priceChangePercent.toFixed(2)}%`;

              if (
                (symbol === "PAXGUSDT" && m.symbol === "XAU") ||
                mSymbol === symbol
              ) {
                let finalPrice = closePrice;
                if (m.symbol === "XAU" && m.pair.includes("EURO"))
                  finalPrice = closePrice * eurRateRef.current;

                const decimals = symbol.includes("BTC")
                  ? 2
                  : closePrice < 1
                    ? 6
                    : 2;
                return {
                  ...m,
                  price: finalPrice.toLocaleString(undefined, {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals,
                  }),
                  isUp,
                  change: changeText,
                };
              }
              return m;
            });
            // Save to cache for the next refresh
            localStorage.setItem(
              "last_market_prices",
              JSON.stringify(newState),
            );
            return newState;
          });
        } catch (e) {}
      };

      ws.onclose = () => {
        if (isMounted) setTimeout(connect, 300); // Rapid 300ms retry
      };
    };

    // WATCHDOG: If no message for 2 seconds, force a reconnection
    watchdogInterval = setInterval(() => {
      if (Date.now() - lastUpdateRef.current > 2000) {
        console.warn("[Watchdog] Connection frozen. Resetting...");
        connect();
      }
    }, 2000);

    // CLEAN EXIT: Tell Binance to close the connection before the page disappears
    const handleUnload = () => {
      if (wsRef.current) wsRef.current.close();
    };
    window.addEventListener("beforeunload", handleUnload);

    connect();

    return () => {
      isMounted = false;
      clearInterval(watchdogInterval);
      window.removeEventListener("beforeunload", handleUnload);
      if (wsRef.current) wsRef.current.close();
    };
  }, []);

  return (
    <div className="majestic-page-wrapper" style={{ paddingBottom: "90px" }}>
      <div className="majestic-header d-flex align-items-center px-3 py-3 justify-content-between">
        <div style={{ width: "40px" }}></div>
        <h1 className="majestic-header-title flex-grow-1 text-center">
          {t("market_list", "Market List")}
        </h1>
        <div style={{ width: "40px" }} className="d-flex justify-content-end">
          <UserLanguageSwitcher />
        </div>
      </div>

      <div className="majestic-container animate-slide-up">
        <div className="market-list-premium">
          {markets.map((market, index) => {
            const IconComponent = CRYPTO_ICONS[market.symbol];
            return (
              <div
                className="market-item-majestic animate-fade-in"
                key={market.id}
                onClick={() =>
                  navigate(
                    `/trade/${market.pair.replace("/", "").toUpperCase()}`,
                  )
                }
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <div className="market-item-left">
                  <div className="coin-icon-majestic">
                    {IconComponent ? (
                      React.createElement(IconComponent, { size: 24 })
                    ) : (
                      <span className="coin-text-fallback">
                        {market.symbol[0]}
                      </span>
                    )}
                  </div>
                  <div className="pair-info-majestic">
                    <div className="pair-name-majestic">{market.pair}</div>
                    <div className="pair-symbol-majestic">{market.symbol}</div>
                  </div>
                </div>
                <div className="market-item-right">
                  <div className="price-info-majestic">
                    <div className="current-price-majestic">{market.price}</div>
                    <div
                      className={`price-change-majestic ${market.isUp ? "up" : "down"}`}
                    >
                      <i
                        className={`bi bi-caret-${market.isUp ? "up" : "down"}-fill me-1`}
                      ></i>
                      {market.change}
                    </div>
                  </div>
                  <i className="bi bi-chevron-right ms-3 opacity-25"></i>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bottom-nav">
        <Link to="/dashboard" className="nav-item">
          <i className="bi bi-house-door"></i>
          <span>{t("home", "Home")}</span>
        </Link>
        <Link to="/markets" className="nav-item active">
          <i className="bi bi-graph-up"></i>
          <span>{t("market", "Market")}</span>
        </Link>
        <Link to="/holdings" className="nav-item">
          <i className="bi bi-pie-chart"></i>
          <span>{t("holdings", "Holdings")}</span>
        </Link>
        <Link to="/profile" className="nav-item">
          <i className="bi bi-person"></i>
          <span>{t("my_account", "My Account")}</span>
        </Link>
      </div>
    </div>
  );
};

export default MarketPage;
